/**
 * Session Feed
 *
 * Displays a list of recent Engage sessions.
 */

import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'

interface Session {
  id: string
  status: string
  channel: string
  started_at: string
  message_count: number
  agent_id?: string
}

interface SessionFeedProps {
  sessions: Session[]
}

export default function SessionFeed({ sessions }: SessionFeedProps) {
  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'voice': return '📞'
      case 'chat': return '💬'
      case 'email': return '📧'
      case 'sms': return '📱'
      default: return '💬'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500'
      case 'ended': return 'bg-gray-400'
      case 'error': return 'bg-red-500'
      default: return 'bg-gray-400'
    }
  }

  const formatRelativeTime = (timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime()
    const minutes = Math.floor(diff / 60000)
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  }

  if (sessions.length === 0) {
    return (
      <div className="py-8 text-center">
        <div className="text-4xl mb-3 opacity-50">💬</div>
        <p className="text-gray-500">No recent sessions</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <AnimatePresence>
        {sessions.slice(0, 5).map((session, index) => (
          <motion.div
            key={session.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ delay: index * 0.05 }}
          >
            <Link
              to={`/tenant/engage/monitor?session=${session.id}`}
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <span className="text-xl">{getChannelIcon(session.channel)}</span>
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${getStatusColor(session.status)}`}
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 group-hover:text-engage-primary transition-colors">
                    {session.agent_id || 'Unassigned'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {session.message_count} messages
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-xs text-gray-400">
                  {formatRelativeTime(session.started_at)}
                </span>
                <span className="block text-xs font-medium text-gray-400 group-hover:text-engage-primary transition-colors">
                  View →
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
